/**
 * 프로젝트 수정 페이지
 */

import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { ArrowLeft } from "lucide-react";
import { createLogger } from "@ict/shared";
import { ProjectForm, type ProjectFormData } from "@/components/project/ProjectForm";
import { PROJECT_QUERY, UPDATE_PROJECT_MUTATION, PROJECTS_QUERY } from "@/lib/graphql/projects";
import { useToast } from "@/contexts/ToastContext";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";

const logger = createLogger({ module: "ProjectEditPage" });

export function ProjectEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data, loading: queryLoading } = useQuery(PROJECT_QUERY, {
    variables: { id },
    skip: !id,
  });

  const [updateProject, { loading: mutationLoading }] = useMutation(UPDATE_PROJECT_MUTATION, {
    refetchQueries: [{ query: PROJECTS_QUERY }, { query: PROJECT_QUERY, variables: { id } }],
    onCompleted: () => {
      showToast("프로젝트가 수정되었습니다.", "success");
      navigate(`/projects/${id}`);
    },
    onError: (error) => {
      logger.error("프로젝트 수정 실패:", error);
      showToast("프로젝트 수정에 실패했습니다.", "error");
    },
  });

  const handleSubmit = async (formData: ProjectFormData) => {
    await updateProject({
      variables: {
        id,
        input: formData,
      },
    });
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/projects");
    }
  };

  if (queryLoading) {
    return <FullScreenSpinner />;
  }

  if (!data?.project) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <p className="text-red-500">프로젝트를 찾을 수 없습니다</p>
        <button onClick={() => navigate("/projects")} className="mt-4 text-blue-600">
          목록으로
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* 헤더 */}
      <div className="border-b bg-white px-4 py-3 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-1.5 -ml-1.5 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
          aria-label="뒤로가기"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold">프로젝트 수정</h1>
          <p className="text-sm text-gray-500 truncate">{data.project.name}</p>
        </div>
      </div>

      {/* 폼 */}
      <ProjectForm
        mode="edit"
        initialData={data.project}
        onSubmit={handleSubmit}
        onCancel={handleBack}
        isLoading={mutationLoading}
      />
    </div>
  );
}

export default ProjectEditPage;
